import React, { useState, useRef, useCallback } from "react";
import Webcam from "react-webcam";
import styles from "../../styles/shap_of_you.module.scss";
import { FaceScanningScreenProps } from "./types";
import { toast } from "react-toastify";

const FaceScanningScreen: React.FC<FaceScanningScreenProps> = ({
  navigateToStep,
  onCapture,
}) => {
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const webcamRef = useRef<Webcam>(null);

  // Camera ready callback
  const handleCameraReady = () => {
    setIsCameraReady(true);
  };

  // Handle camera error
  const handleCameraError = (error: string | DOMException) => {
    console.error("Camera error:", error);
    toast.error("Failed to access camera. Please check permissions.");
    // Navigate to permission denied screen
    navigateToStep("permissionDenied");
  };

  // Capture face from webcam
  const captureFace = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc && onCapture) {
        // Immediately freeze to captured image
        setCapturedImage(imageSrc);
        onCapture(imageSrc);
      } else {
        toast.error("Failed to capture image. Please try again.");
      }
    }
  }, [webcamRef, onCapture]);

  return (
    <div className="d-md-none w-100">
      <div className={styles.wrapper_face}>
        {/* Live Camera Feed */}
        <div className={styles.image} style={{ position: "relative" }}>
          {/* Show loader while camera is initializing */}
          {!isCameraReady && !capturedImage && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                zIndex: 10,
              }}
            >
              <div style={{ textAlign: "center", color: "#fff" }}>
                <div
                  className="spinner-border text-light mb-3"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                >
                  <span className="visually-hidden">Accessing camera...</span>
                </div>
                <p style={{ fontSize: "1rem", fontWeight: "bold" }}>
                  Accessing your camera...
                </p>
              </div>
            </div>
          )}

          <Webcam
            ref={webcamRef}
            audio={false}
            mirrored={true}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              facingMode: "user",
              width: { ideal: 640, min: 480 },
              height: { ideal: 480, min: 360 },
              aspectRatio: { ideal: 4 / 3 },
            }}
            onUserMedia={handleCameraReady}
            onUserMediaError={handleCameraError}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
              display: capturedImage ? "none" : "block",
            }}
          />

          {/* Frozen captured image — shown immediately on scan */}
          {capturedImage && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={capturedImage}
              alt="Captured face"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center center",
                zIndex: 1,
              }}
            />
          )}

          {/* Face placement guide overlay - head & shoulders silhouette */}
          {isCameraReady && (
            <>
              {/*
                Single SVG covers entire screen.
                - Dark overlay with silhouette cutout (mask)
                - Dashed outline drawn with same path
                Both share the SAME coordinate system so they align perfectly.

                Shape: rounded head -> narrows at neck -> flares to shoulders
                Matches the reference design image.
              */}
              {/* <svg
                width="100%"
                height="100%"
                viewBox="0 0 375 812"
                preserveAspectRatio="xMidYMid slice"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <defs>
                  <clipPath id="soy-silhouette-clip">
                    <path
                      d="
                      M 187.5 180
                      C 245 180, 280 230, 280 300
                      C 280 355, 255 395, 222 410
                      Q 218 422, 218 445
                      Q 218 475, 330 515
                      L 330 572
                      L 45 572
                      L 45 515
                      Q 157 475, 157 445
                      Q 157 422, 137 410
                      C 120 395, 95 355, 95 300
                      C 95 230, 130 180, 187.5 180
                      Z
                    "
                    />
                  </clipPath>
                </defs>

                <path
                  fillRule="evenodd"
                  fill="rgba(0, 0, 0, 0.85)"
                  d="
                    M 0 0 L 375 0 L 375 812 L 0 812 Z
                    M 187.5 180
                    C 245 180, 280 230, 280 300
                    C 280 355, 255 395, 222 410
                    Q 218 422, 218 445
                    Q 218 475, 330 515
                    L 330 572
                    L 45 572
                    L 45 515
                    Q 157 475, 157 445
                    Q 157 422, 137 410
                    C 120 395, 95 355, 95 300
                    C 95 230, 130 180, 187.5 180
                    Z
                  "
                />

                <path
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.9)"
                  strokeWidth="2.5"
                  strokeDasharray="12 10"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  d="
                    M 187.5 180
                    C 245 180, 280 230, 280 300
                    C 280 355, 255 395, 222 410
                    Q 218 422, 218 445
                    Q 218 475, 330 515
                    L 330 572
                    L 45 572
                    L 45 515
                    Q 157 475, 157 445
                    Q 157 422, 137 410
                    C 120 395, 95 355, 95 300
                    C 95 230, 130 180, 187.5 180
                    Z
                  "
                />
              </svg> */}
              <svg
                style={{
                  height: "100%",
                  width: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                  zIndex: 1,
                }}
                viewBox="0 0 393 852"
                preserveAspectRatio="xMidYMid slice"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <foreignObject x="0" y="0" width="0" height="0">
                  <div
                    style={{
                      backdropFilter: "blur(16px)",
                      clipPath: "url(#bgblur_0_4001_2030_clip_path)",
                      height: "100%",
                      width: "100%",
                    }}
                  ></div>
                </foreignObject>
                <path
                  d="M393 852H0V0H393V852ZM196.5 131C105.649 131 32 223.539 32 337.691C32.0001 394.192 50.0459 445.397 79.2871 482.707C85.431 493.711 91.4999 508.552 91.5 522.741C91.5 549.682 87.5 570.881 70.5 584.572C56.9001 595.525 32.8334 612.396 22.5 619.462V710L371.5 709.559V623.438C358.554 618.579 316.931 600.825 312 584.572C307.07 568.32 302.779 548.063 302 534.225C301.321 522.156 302.967 500.569 316.667 478.839C318.693 476.117 320.661 473.326 322.568 470.47C322.874 470.08 323.185 469.691 323.5 469.302H323.346C346.866 433.554 361 387.697 361 337.691C361 223.539 287.351 131 196.5 131Z"
                  fill="#000000"
                  fillOpacity="0.75"
                />
                <path
                  d="M197 131C287.851 131 361.5 223.68 361.5 338.007C361.5 388.089 347.366 434.016 323.846 469.819H324C323.685 470.209 323.374 470.598 323.068 470.989C321.161 473.85 319.193 476.644 317.167 479.37C303.467 501.133 301.821 522.753 302.5 534.84C303.279 548.7 307.57 568.988 312.5 585.265C317.43 601.543 360.054 619.017 373 623.883V710H23V620.209C33.3333 613.131 57.4 596.235 71 585.265C88 571.553 92 550.322 92 523.34C92 509.129 85.9311 494.265 79.7871 483.244C50.5458 445.877 32.5 394.594 32.5 338.007C32.5 223.68 106.149 131 197 131Z"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="12 12"
                />
                <defs>
                  <clipPath
                    id="bgblur_0_4001_2030_clip_path"
                    transform="translate(0 0)"
                  >
                    <path d="M393 852H0V0H393V852ZM196.5 131C105.649 131 32 223.539 32 337.691C32.0001 394.192 50.0459 445.397 79.2871 482.707C85.431 493.711 91.4999 508.552 91.5 522.741C91.5 549.682 87.5 570.881 70.5 584.572C56.9001 595.525 32.8334 612.396 22.5 619.462V710L371.5 709.559V623.438C358.554 618.579 316.931 600.825 312 584.572C307.07 568.32 302.779 548.063 302 534.225C301.321 522.156 302.967 500.569 316.667 478.839C318.693 476.117 320.661 473.326 322.568 470.47C322.874 470.08 323.185 469.691 323.5 469.302H323.346C346.866 433.554 361 387.697 361 337.691C361 223.539 287.351 131 196.5 131Z" />
                  </clipPath>
                </defs>
              </svg>
              {/* Instruction text at top */}
              <div
                style={{
                  position: "absolute",
                  top: "50px",
                  left: "49%",
                  transform: "translateX(-49%)",
                  color: "#fff",
                  textAlign: "center",
                  fontSize: "15px",
                  // fontWeight: "600",
                  zIndex: 4,
                  fontFamily: "Sweet Sans Pro Regular",
                  backgroundColor: "transparent",
                  padding: "10px 20px",
                  borderRadius: "25px",
                  // backdropFilter: "blur(10px)",
                  // border: "1px solid rgba(255, 255, 255, 0.2)",
                  whiteSpace: "nowrap",
                }}
              >
                Position your face in the outline
              </div>
              {/* Instruction text below the shape */}
              {/* <div
                style={{
                  position: "absolute",
                  top: "80%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#fff",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "400",
                  zIndex: 4,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  maxWidth: "80%",
                  whiteSpace: "nowrap",
                }}
              >
                Keep neck visible for best results
              </div> */}
            </>
          )}
        </div>

        {/* Capture Button */}
        {isCameraReady && !capturedImage && (
          <div
            style={{
              position: "absolute",
              bottom: "40px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 5,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              padding: "0 20px",
            }}
          >
            <button
              onClick={captureFace}
              style={{
                minWidth: "290px",
                width: "100%",
                maxWidth: "100%",
                background: "rgba(210, 35, 42, 1)",
                color: "#fff",
                border: "none",
                padding: "16px",
                borderRadius: "40px",
                fontSize: "20px",
                fontFamily: "Sweet Sans Pro Medium",
                cursor: "pointer",
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.transform = "scale(0.95)";
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <svg
                className="me-2"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 3H5C3.89543 3 3 3.89543 3 5V7"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 3H19C20.1046 3 21 3.89543 21 5V7"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 9V11"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 9V11"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 15C9 15 10 16 12 16C14 16 15 15 15 15"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 21H5C3.89543 21 3 20.1046 3 19V17"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 21H19C20.1046 21 21 20.1046 21 19V17"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Scan Face</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceScanningScreen;
